import type { HeroEntityKind } from './openEntityDrawer';

/**
 * 24-char Mongo ObjectId hex class. Use as a regex fragment — it's
 * a raw character class so it can be embedded inside larger patterns
 * (e.g. `${OBJECT_ID_CLASS}`). Do NOT call `.slice(1, -1)` on it; an
 * earlier version did that and silently broke every match — see
 * debug session c66c60.
 */
const OBJECT_ID_CLASS = '[a-fA-F0-9]{24}';

export interface ParsedHeroEntityLink {
  kind: HeroEntityKind;
  id: string;
}

const KIND_ALIASES: Record<string, HeroEntityKind> = {
  client: 'client',
  contact: 'contact',
  order: 'order',
  driver: 'driver',
  vehicle: 'vehicle',
  saved_location: 'saved_location',
  savedlocation: 'saved_location',
  savedlocation_id: 'saved_location',
  location: 'saved_location',
  place: 'saved_location',
  saved: 'saved_location',
  invoice: 'invoice',
  account: 'account',
  transaction: 'transaction',
  tx: 'transaction',
  txn: 'transaction',
};

const normalizeKind = (raw: string): HeroEntityKind | null => {
  const key = raw.toLowerCase().replace(/-/g, '_');
  return KIND_ALIASES[key] ?? null;
};

const HERO_SCHEME_RE = new RegExp(
  `^hero:\\/\\/([a-zA-Z_-]+)\\/(${OBJECT_ID_CLASS})(?:\\/.*)?(?:\\?.*)?$`,
  'i',
);

const parseHeroScheme = (href: string): ParsedHeroEntityLink | null => {
  const match = href.trim().match(HERO_SCHEME_RE);
  if (!match) return null;
  const kind = normalizeKind(match[1]);
  if (!kind) return null;
  return { kind, id: match[2] };
};

const OBJECT_ID_ONLY_RE = new RegExp(`^${OBJECT_ID_CLASS}$`, 'i');

/** Parse dashboard / app URLs the model sometimes emits instead of hero:// links. */
const parseAppUrl = (href: string): ParsedHeroEntityLink | null => {
  try {
    const url = href.startsWith('http') ? new URL(href) : new URL(href, 'https://app.tryhero.io');
    const path = url.pathname.replace(/\/+$/, '');
    const q = url.searchParams;

    const clientFromQuery = q.get('clientId');
    if (clientFromQuery?.match(OBJECT_ID_ONLY_RE)) {
      return { kind: 'client', id: clientFromQuery };
    }

    const orderFromQuery = q.get('orderId');
    if (orderFromQuery?.match(OBJECT_ID_ONLY_RE)) {
      return { kind: 'order', id: orderFromQuery };
    }

    const locationFromQuery =
      q.get('savedLocationId') ?? q.get('locationId') ?? q.get('saved_location_id');
    if (locationFromQuery?.match(OBJECT_ID_ONLY_RE)) {
      return { kind: 'saved_location', id: locationFromQuery };
    }

    const patterns: Array<{ re: RegExp; kind: HeroEntityKind }> = [
      { re: new RegExp(`/crm/clients/(${OBJECT_ID_CLASS})`, 'i'), kind: 'client' },
      { re: new RegExp(`/clients/(${OBJECT_ID_CLASS})`, 'i'), kind: 'client' },
      { re: new RegExp(`/contacts/(${OBJECT_ID_CLASS})`, 'i'), kind: 'contact' },
      { re: new RegExp(`/drivers/(${OBJECT_ID_CLASS})`, 'i'), kind: 'driver' },
      { re: new RegExp(`/vehicles/(${OBJECT_ID_CLASS})`, 'i'), kind: 'vehicle' },
      { re: new RegExp(`/saved-locations/(${OBJECT_ID_CLASS})`, 'i'), kind: 'saved_location' },
      { re: new RegExp(`/dashboard/order/(?:view/)?(${OBJECT_ID_CLASS})`, 'i'), kind: 'order' },
      { re: new RegExp(`/my-orders/(?:view/)?(${OBJECT_ID_CLASS})`, 'i'), kind: 'order' },
      { re: new RegExp(`/invoices/(${OBJECT_ID_CLASS})`, 'i'), kind: 'invoice' },
      { re: new RegExp(`/transactions/(${OBJECT_ID_CLASS})`, 'i'), kind: 'transaction' },
    ];

    for (const { re, kind } of patterns) {
      const m = path.match(re);
      if (m?.[1]) return { kind, id: m[1] };
    }
  } catch {
    return null;
  }
  return null;
};

/**
 * Parse entity deep links from assistant markdown — supports
 * `hero://client/{id}` and common dashboard URL shapes.
 */
export const parseHeroEntityLink = (href: string | undefined): ParsedHeroEntityLink | null => {
  if (!href?.trim()) return null;
  const trimmed = href.trim();
  return parseHeroScheme(trimmed) ?? parseAppUrl(trimmed);
};
